<template>
  <div class="item">
    <div v-if="!isEmployee" class="cart-buttons-panel">
      <a class="add-to-cart-btn bo_remove-action"
         @click="removeItem">
        <img src="@/assets/images/remove.png" width="21" title="Slett produkt"/>
      </a>
      <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
    </div>
    <div class="item-col-left">
      <span v-if="item.imageUrl"> <img :src="item.imageUrl" width="100" height="100"/> </span>
      <span v-else class="bo_item-image-placeholder"><font-awesome-icon :icon="['fas', 'utensils']"/></span>
    </div>
    <div class="item-col-right">
      <div class="flex-box">
        <div>
          <span class="item-title">{{ item.menuNr }} &nbsp;&nbsp;  {{item.name}} </span>
          <span v-if="soldOutBadge" class="bo_sold-out-badge ms-1" :title="soldOutBadge.title">{{ soldOutBadge.label }}</span>
          <br/>
          <span class="description">{{item.description}}</span><br/>
          <div v-if="item.prepTimeMinutes" class="mb-2">
            <span>Tilberedningstid: <span style="color: #78b13f;">{{ item.prepTimeMinutes }} min</span></span>
            <span v-if="item.readyMade"> (hyllevare)</span>
          </div>
          <div v-if="hasLimitedAvailability" class="mb-2">
            <span>Tilgjengelig: </span><span v-for="(day, index) in item.availableOnDays" :key="index">{{ ' ' + getDayName(day) }}</span>
          </div>
          <!-- Only shown when the merchant actually has more than one location; for a single-location
               merchant every product is trivially "everywhere" and the line is pure noise. -->
          <div v-if="locations.length > 1" class="mb-2 bo_item-locations">
            <span v-if="locationNames.length === 0" class="bo_item-location-badge bo_item-location-badge--none"
                  title="Produktet vises ingen steder - ingen avdeling er huket av">Ingen avdelinger</span>
            <span v-else-if="locationNames.length === locations.length"
                  class="bo_item-location-badge">Alle avdelinger</span>
            <template v-else>
              <span v-for="name in locationNames" :key="name" class="bo_item-location-badge">{{ name }}</span>
            </template>
          </div>
          <!-- Which channels the product is sold on (ChannelUtil) — only for a merchant who chooses channels at all,
               as the edit form's «Tilgjengelig på følgende kanaler». -->
          <div v-if="usesChannels" class="mb-2 bo_item-channels">
            <span v-if="channelIds.length === 0" class="bo_item-channel-badge bo_item-channel-badge--none"
                  title="Produktet kan ikke bestilles noe sted - ingen kanal er huket av">Ingen kanaler</span>
            <span v-else-if="isEveryChannel" class="bo_item-channel-badge"
                  :title="everyChannelTitle">Alle kanaler</span>
            <template v-else>
              <span v-for="channel in channels" :key="channel.id" class="bo_item-channel-badge">{{ channel.name }}</span>
            </template>
          </div>
          <div v-if="useFunctionality != 'WEBSITE'">
            <div v-if="item.itemType != 'DELIVERY_SERVICE'">
              <div v-if="item.openPrice">
                  <span class="price">Åpen pris</span> <span>(prisen settes ved bestilling)</span>
              </div>
              <div v-else>
                <div v-if="showTakeawayPrice">
                    <span class="price">kr&nbsp;</span> <span class="price"> {{item.priceTakeaway}} </span> <span>(med {{vat.takeOut}}% mva for takeaway)</span>
                </div>
                <!-- No dine-in price is a legitimate state: an item sold takeaway only. Show nothing
                     rather than the "kr NaN" an unentered price used to render as. -->
                <div v-if="showDineInPrice">
                    <span class="price">kr&nbsp;</span> <span class="price"> {{item.priceDineInWithVat}} </span> <span>(med {{vat.dineIn}}% mva for spise her)</span>
                </div>
              </div>
            </div>
            <div v-else>
              <span class="price">kr&nbsp;</span> <span class="price"> {{item.priceDineInWithVat}} </span> <span>(med {{vat.dineIn}}% mva for tjeneste)</span>
            </div>
          </div>
        </div>
        <div v-if="!isEmployee" class="cart-buttons-panel">
          <a class="add-to-cart-btn" style="cursor:pointer"
             @click="goToItemEditPanel()">
            <img src="@/assets/images/edit.png" width="21" title="Rediger produkt"/>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ConfirmDialogue from '@/components/other/ConfirmDialogue.vue'
import ProductService from "@/services/product/ProductService";
import ChannelUtil from "@/util/ChannelUtil";
import MerchantUtil from "@/util/MerchantUtil";
import SoldOutUtil from "@/util/SoldOutUtil";
import UserUtil from "@/util/UserUtil";

export default {
  components: {ConfirmDialogue},
  props: {
    item: Object,
    vat: Object,
    soldOut: { type: Object, default: null }, //SoldOutUtil.index: where each thing is sold out, per location
    locations: { type: Array, default: () => [] },
    categoryChannelIds: { type: Array, default: null } //the category's availableForChannelIds, which bound the product's
  },
  emits: ['openPanel', 'refresh'],
  computed: {
    usesChannels(){
      return ChannelUtil.merchantUsesChannels();
    },
    channelIds(){
      return ChannelUtil.channelIdsOf(this.item, this.categoryChannelIds);
    },
    channels(){
      return ChannelUtil.channelsOf(this.channelIds);
    },
    isEveryChannel(){
      return ChannelUtil.isEveryChannel(this.channelIds);
    },
    everyChannelTitle(){
      return ChannelUtil.CHANNELS.map(channel => channel.name).join(', ');
    },
    /**
     * The names behind item.availableAtLocationIds, in the merchant's own location order.
     *
     * Mapped through the locations list rather than read off the item, so a stale id left behind by a
     * deleted location simply disappears instead of rendering as a raw uuid.
     */
    /**
     * Whether to show the "spise her" price at all.
     *
     * Three things must hold: the merchant sells dine-in, THIS product is sold dine-in, and a price
     * exists. The middle one is the important one and cannot be read off the price: the backend fills a
     * missing dine-in price with the takeaway price so the terminals never compute NaN, so a
     * takeaway-only product does have a number stored — it just should not be shown one. The item's own
     * availableForOrderTypes is the merchant's actual statement about that.
     *
     * An item with no availableForOrderTypes at all is old data that predates the field; showing the
     * price is the previous behaviour and the safer default there.
     */
    /** Mirror of showDineInPrice: a dine-in-only product should not show a takeaway price either. */
    showTakeawayPrice(){
      if(this.item.priceTakeaway === '' || this.item.priceTakeaway === undefined){
        return false;
      }
      let orderTypes = this.item.availableForOrderTypes;
      return !orderTypes || orderTypes.length === 0 || orderTypes.indexOf('TAKEAWAY') > -1;
    },
    showDineInPrice(){
      let merchantSellsDineIn = this.useFunctionality == 'POS' || this.useFunctionality == 'KIOSK_TAKEAWAY'
          || this.useFunctionality == 'ALL';
      if(!merchantSellsDineIn || this.item.priceDineInWithVat === '' || this.item.priceDineInWithVat === undefined){
        return false;
      }
      let orderTypes = this.item.availableForOrderTypes;
      return !orderTypes || orderTypes.length === 0 || orderTypes.indexOf('DINE_IN') > -1;
    },
    locationNames(){
      const ids = this.item.availableAtLocationIds || [];
      return this.locations.filter(location => ids.indexOf(location.id) > -1)
                           .map(location => location.name);
    },
    useFunctionality(){
      return MerchantUtil.useFunctionality();
    },
    isEmployee(){
      return UserUtil.isEmployee();
    },
    /**
     * «Utsolgt», or «Utsolgt: Grefsen» when it has run out at some locations and not others. Sold out at a
     * location means the item itself is marked there, or every one of its variants is.
     */
    soldOutBadge(){
      return SoldOutUtil.itemBadge(this.item, this.soldOut);
    },
    hasLimitedAvailability(){
      //Only show the availability line when it is an exception, available every day is the normal case
      let days = this.item.availableOnDays;
      if(!days || days.length === 0){
        return false;
      }
      let weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
      for(let i = 0; i < weekdays.length; i++){
        if(!days.includes(weekdays[i])){
          return true; //At least one day the item is not available
        }
      }
      return false;
    }
  },
  methods: {
    getDayName(day){
      if(day === 'MONDAY'){
        return 'Man';
      } else if(day == 'TUESDAY'){
        return 'Tir';
      } else if(day == 'WEDNESDAY'){
        return 'Ons';
      } else if(day == 'THURSDAY'){
        return 'Tor';
      } else if(day == 'FRIDAY'){
        return 'Fre';
      } else if(day == 'SATURDAY'){
        return 'Lør';
      } else if(day == 'SUNDAY'){
        return 'Søn';
      } else if(day == 'HOLIDAY'){
        return 'Hellig dag';
      }
      return 'DAY_NOT_DEFINED';
    },
    goToItemEditPanel(){
       this.$emit('openPanel', 'itemEdit', { categoryId: this.item.categoryId, itemId: this.item.id });
    },
    async removeItem(){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Slett produktet "' + this.item.name + '"',
        message: 'Vil du slette produktet "' + this.item.name + '"?',
        okButton: 'Slett',
        cancelButton: 'Avbryt'
      })
      if(ok) {
        ProductService.deleteItem(this.item).then(
            (response) => {
              if(response.data != null){
                this.$emit('refresh');
              }else {
                console.log("Deleting item failed");
              }
            },
            (error) => {
              console.log(error);
            }
        );
      } else {
        //Cancel, do nothing
      }
    }
  }
}
</script>
